import { createAsync, useParams } from '@solidjs/router';
import { Show, Switch, Match, createMemo, useContext, Suspense, Index } from 'solid-js';
import {
	Button,
	Errors,
	Form,
	Container,
	Heading,
	Section,
	Grid,
	GridFive,
	GridSeven,
	Receipt,
	AccessCircleLogo,
	MessageBar,
} from '@troon/ui';
import { Title } from '@solidjs/meta';
import { useIsEnabled, useTrackEvent } from '@troon/analytics';
import { twJoin } from '@troon/tailwind-preset/merge';
import { IconSquareWarning } from '@troon/icons/square-warning';
import dayjs from '@troon/dayjs';
import { cachedGet } from '@troon/api-client';
import { IconInfo } from '@troon/icons/info';
import { ReservationState, ReservationUserState, gql, mutationAction, useMutation } from '../../../../graphql';
import { useUser } from '../../../../providers/user';
import { ReservationCtx } from '../../../../providers/reservation';
import { FacilityLocation } from '../../../../components/facility/location';
import { FacilityReservationHeader } from '../../../../components/facility/reservation-header';
import { FrequentlyAskedQuestions } from '../../../../components/faqs';
import { GuestPassBanner } from '../../../../components/guest-pass-banner';
import { CancelReservationDialog } from '../../../../components/reservation/dialogs/cancel-reservation-dialog';
import { LeaveReservationDialog } from '../../../../components/reservation/dialogs/leave-reservation-dialog';
import { ReservationCalendarDialog } from '../../../../components/reservation/dialogs/reservation-calendar-dialog';
import { ReservationAccessUpsell } from '../../../../components/upsells/access-reservation';
import { ReservationPlayerSlot } from './components/reservation-player';
import type { RouteSectionProps } from '@solidjs/router';

export default function Reservation(props: RouteSectionProps) {
	const params = useParams();
	const user = useUser();
	const data = useContext(ReservationCtx)!;
	const trackEvent = useTrackEvent();
	const postBookingRedemptionEnabled = useIsEnabled('post-booking-redemption');

	const acceptAction = useMutation(handleAccept);
	const rejectAction = useMutation(handleReject);

	const paymentInfo = createAsync(() => getPaymentInfo({ path: { id: props.params.reservationId! } }));

	const reservationUser = createMemo(() => {
		return data()?.reservation.users.find((resUser) => resUser.user?.id === user()?.id);
	});

	const isPastStartTime = createMemo(() => dayjs().isSameOrAfter(data.latest?.reservation.teeTime.epoch));

	const canModify = createMemo(() => {
		return !!(
			!isPastStartTime() &&
			data.latest?.reservation.owner.id === user()?.id &&
			data.latest?.reservation.state !== 'CANCELLED' &&
			(paymentInfo.latest?.cancellationInfo.isCancellable || dayjs().isBefore(data.latest?.reservation.cancelBy?.epoch))
		);
	});
	const guestPassesRemaining = () => data.latest?.owner?.guestPasses.available.length ?? 0;

	const reservationUsers = createMemo(() => data.latest?.reservation.users);
	const canUsePostBookingRedemption = (reservationUserId: string) => {
		const currentReservationUser = reservationUser();
		return (
			postBookingRedemptionEnabled &&
			!!currentReservationUser &&
			currentReservationUser.id === reservationUserId &&
			currentReservationUser.state === ReservationUserState.Accepted &&
			!!currentReservationUser.canRedeemPoints
		);
	};

	return (
		<>
			<Container size={data()?.reservation.state === ReservationState.Cancelled ? 'small' : undefined} class="py-8">
				<>
					<Title>
						Tee time reservation at{' '}
						{data.latest?.reservation.facility.name ?? data.latest?.reservation.course.name ?? ''} | Golf with Access
					</Title>

					<Show when={data()?.reservation.state === ReservationState.Cancelled}>
						<MessageBar appearance="warning">
							<MessageBar.Icon>
								<IconInfo />
							</MessageBar.Icon>
							<MessageBar.Content>This tee time has been cancelled.</MessageBar.Content>
						</MessageBar>
					</Show>

					<Show when={data.latest?.reservation}>
						{(reservation) => (
							<FacilityReservationHeader
								bordered
								epoch={reservation().teeTime.epoch}
								timezone={reservation().teeTime.tz}
								slug={reservation().facility.slug}
								name={reservation().facility.name}
								holeCount={reservation().holeCount}
								playerCount={reservation().playerCount}
								includesCart={reservation().includesCart}
								courseName={reservation().course.name}
							>
								<div class="flex flex-row flex-wrap items-stretch justify-end gap-4">
									<Switch>
										<Match when={isPastStartTime() || data.latest?.reservation.state === ReservationState.Cancelled}>
											{null}
										</Match>
										<Match when={reservationUser()?.state === ReservationUserState.Invited}>
											<Form action={acceptAction} document={acceptInvitationMutation} method="post">
												<input type="hidden" name="reservationUserId" value={reservationUser()?.id} />
												<Errors />
												<Button type="submit">Accept invitation</Button>
											</Form>
											<Form action={rejectAction} document={rejectInvitationMutation} method="post">
												<input type="hidden" name="reservationUserId" value={reservationUser()?.id} />
												<input type="hidden" name="__reservationId" value={params.reservationId} />
												<Errors />
												<Button appearance="secondary" action="danger" type="submit">
													Reject invitation
												</Button>
											</Form>
										</Match>
										<Match when={canModify() && paymentInfo.latest?.cancellationInfo}>
											{(cancellationInfo) => (
												<CancelReservationDialog
													reservationId={reservation().id}
													phone={reservation().facility.phone ?? ''}
													cancelBy={cancellationInfo().cancelBy?.epoch}
													cancellationPolicy={cancellationInfo().displayMessage}
												/>
											)}
										</Match>
										<Match when={!canModify() && data.latest?.reservation.state === 'ACTIVE'}>
											<LeaveReservationDialog
												disabled={isPastStartTime()}
												reservationUserId={reservationUser()?.id}
												reservationId={params.reservationId!}
											/>
										</Match>
									</Switch>
									<Show
										when={
											reservationUser()?.state !== 'INVITED' &&
											data.latest?.reservation.state !== 'CANCELLED' &&
											!isPastStartTime()
										}
									>
										<ReservationCalendarDialog calendarEvent={data.latest?.reservation.calendarEvent ?? {}} />
									</Show>
								</div>
							</FacilityReservationHeader>
						)}
					</Show>

					<Grid>
						<GridSeven
							class={twJoin('flex flex-col gap-8', data()?.reservation.state === 'CANCELLED' && 'lg:col-span-12')}
						>
							<Suspense>
								<Show when={data()?.reservation.state !== 'CANCELLED' && data()?.owner?.accessUpsell}>
									{(upsell) => <ReservationAccessUpsell savings={upsell().savings.displayValue} />}
								</Show>
							</Suspense>
							<GuestPassBanner remaining={guestPassesRemaining()} />

							<Section layout="tight">
								<Show when={data()?.reservation.facility.supportsAccessPoints && !user()?.accessRewardsOptIn}>
									<MessageBar appearance="brand">
										<MessageBar.Icon>
											<AccessCircleLogo class="size-8" color="brand" />
										</MessageBar.Icon>
										<MessageBar.Content>
											You need to transfer your Troon Rewards points to Access to earn points for this round.
										</MessageBar.Content>
										<MessageBar.Action
											href="/account/transfer-points"
											onClick={() => trackEvent('upsellClicked', { productType: 'points', location: 'reservation' })}
										>
											Transfer
										</MessageBar.Action>
									</MessageBar>
								</Show>

								<Heading as="h2" size="h4">
									Invite & Manage Players
								</Heading>
								<p>
									Invite the other players in your group to join this reservation. We’ll send you a notification when
									they join.
								</p>

								<ul class="flex flex-col gap-2">
									<Index each={reservationUsers()}>
										{(reservationUser, index) => (
											<li class="rounded-lg border border-neutral p-4 md:py-6">
												<ReservationPlayerSlot
													userSlot={index + 1}
													canModify={canModify()}
													user={reservationUser().user}
													userState={reservationUser().state}
													guestPass={reservationUser().guestPass}
													isHost={reservationUser().user?.id === data.latest?.reservation.owner.id}
													feeDisplayValue={reservationUser().fee.displayValue}
													availableGuestPassCount={guestPassesRemaining()}
													availableGuestPass={
														data()?.owner?.guestPasses.available[0]
															? {
																	id: data()!.owner!.guestPasses.available[0]!.id,
																	status: data()!.owner!.guestPasses.available[0]!.status,
																}
															: undefined
													}
													guestPassDiscount={data()?.reservation.guestPassDiscount?.displayValue}
													isPrepaid={data.latest?.reservation.isPrePaid ?? false}
													rateType={reservationUser().rateType}
													reservationUserId={reservationUser().id}
													reservationId={props.params.reservationId!}
													hostRateType={data.latest?.reservation.bookingRateType ?? ''}
													canUsePostBookingRedemption={canUsePostBookingRedemption(reservationUser().id)}
												/>
											</li>
										)}
									</Index>
								</ul>
							</Section>

							<Section layout="tight">
								<Heading as="h2" size="h4">
									Frequently Asked Questions
								</Heading>
								<div class="rounded-lg border border-neutral">
									<FrequentlyAskedQuestions
										id={
											(data()?.reservation.playerCount ?? 0) === 1
												? 'access-individual-booking-faqs'
												: 'access-reservation-details-multiple-player-faqs'
										}
									/>
								</div>
							</Section>

							<Section layout="tight">
								<Heading as="h2" size="h4">
									Facility info
								</Heading>
								<Show when={data.latest?.reservation.facility}>
									{(facility) => (
										<FacilityLocation
											address={facility().address}
											courses={[data.latest!.reservation.course.name]}
											slug={facility().slug}
											name={facility().name}
											phone={facility().phone}
											website={facility().website}
										/>
									)}
								</Show>
							</Section>
						</GridSeven>

						<Show when={data.latest?.reservation.state !== 'CANCELLED'}>
							<GridFive>
								<div class="sticky top-24 flex flex-col gap-y-8">
									<div class="flex flex-col gap-y-4 rounded-lg border border-neutral-500 p-4 md:p-8">
										<div class="flex flex-col gap-4">
											<Heading as="h2" size="h5">
												Payment Info
											</Heading>

											<Show when={paymentInfo.latest?.receipt}>
												{(receipt) => (
													<Receipt
														items={receipt().items.map((i) => ({ label: i.label, amount: i.displayAmount }))}
														totals={receipt().totals.map((i) => ({ label: i.label, amount: i.amount.displayValue }))}
														totalDisclaimer="All amounts displayed are subject to additional taxes and fees, which may be added at check in."
														disclaimer={receipt().disclaimer || undefined}
														reimbursement={
															receipt().reimbursement
																? {
																		label: 'Reimbursed at check-in',
																		amount: receipt().reimbursement!.amount?.displayValue,
																		disclaimer: receipt().reimbursement!.disclaimer,
																	}
																: undefined
														}
													/>
												)}
											</Show>

											<Suspense>
												<Show when={paymentInfo.latest?.accessPointsEarned}>
													{(points) => (
														<p class="flex items-center gap-4 rounded-lg bg-brand-100 p-4 text-sm text-brand-600">
															<AccessCircleLogo class="size-8 shrink-0 text-brand" />
															<span>
																You’ll earn <b>{points()}</b> points when you pay for this round.
															</span>
														</p>
													)}
												</Show>
											</Suspense>
										</div>
									</div>

									<div class="flex flex-col gap-2">
										<Heading as="h3" size="h5">
											<IconSquareWarning class="text-2xl text-brand" /> Cancellation policy
										</Heading>
										<p>{paymentInfo.latest?.cancellationInfo.displayMessage}</p>
									</div>
								</div>
							</GridFive>
						</Show>
					</Grid>
				</>
			</Container>
		</>
	);
}

const getPaymentInfo = cachedGet('/v1/reservations/{id}/payment-info');

export const route = { info: { nav: { hero: false } } };

const acceptInvitationMutation = gql(`mutation acceptReservationInvite($reservationUserId: String!) {
	acceptReservationInvite(reservationUserId: $reservationUserId) { id }
}`);

const handleAccept = mutationAction(acceptInvitationMutation, {
	revalidates: ['/v0/reservations/{id}', '/v1/reservations/{id}/payment-info'],
	track: {
		event: 'acceptInvitation',
		transform: (data) => ({ reservationUserId: data.get('reservationUserId') as string }),
	},
});

const rejectInvitationMutation = gql(`mutation rejectReservationInvite($reservationUserId: String!) {
	rejectReservationInvite(reservationUserId: $reservationUserId) { ok }
}`);

const handleReject = mutationAction(rejectInvitationMutation, {
	redirect: '/',
	toast: 'You have declined this invitation.',
	track: {
		event: 'rejectInvitation',
		transform: (data) => ({ reservationId: data.get('__reservationId') as string }),
	},
});
